import React from 'react';
import { Styles } from './style';
import Header from '@components/Header';
import Navbar from '@components/Navbar';
import Footer from '@components/Footer/Footer';
import ElitePrimeLogo from '@assets/images/Logotipo/Elite Prime_Logo_FundoPreto.png';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { useMediaQuery, useTheme } from '@mui/material';
import { useContactStore } from '@stores/Contact/Contact';
import { useMetadataStore } from '@stores/Metadata/Metadata';
import { maskPhone, isValidPhone, isValidEmail } from '@utils/masks';
import FeedbackModal from '@components/FeedbackModal';
import Loading from '@components/Loading/Loading';
import SEO from '@components/SEO';

interface ContactFormData {
  name: string;
  email: string;
  phone: string;
  subject: string;
  message: string;
}

const ContactScreen: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { submitContact } = useContactStore();
  const { configuracoes, fetchConfiguracoes } = useMetadataStore();

  React.useEffect(() => {
    fetchConfiguracoes();
  }, [fetchConfiguracoes]);

  const endereco =
    configuracoes?.endereco_empresa ??
    'Rua Bonifácio de Oliveira Cassú, 284 - Éden Sorocaba/SP';
  const whatsapp = configuracoes?.whatsapp_empresa ?? '(15) 98137-0057';
  const emailEmpresa =
    configuracoes?.email_empresa ?? 'eliteprimeimobiliaria@gmail.com';
  const logoUri = configuracoes?.footer_logo_uri || ElitePrimeLogo;
  const [submitting, setSubmitting] = React.useState(false);
  const [phoneError, setPhoneError] = React.useState('');
  const [emailError, setEmailError] = React.useState('');
  const [modal, setModal] = React.useState<{
    open: boolean;
    variant: 'success' | 'error';
    title: string;
    message: string;
  }>({
    open: false,
    variant: 'success',
    title: '',
    message: ''
  });

  const [formData, setFormData] = React.useState<ContactFormData>({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    if (name === 'phone') {
      const masked = maskPhone(value);
      setFormData((prev) => ({ ...prev, phone: masked }));
      if (phoneError)
        setPhoneError(!masked || isValidPhone(masked) ? '' : phoneError);
      return;
    }
    if (name === 'email') {
      setFormData((prev) => ({ ...prev, email: value }));
      if (emailError) setEmailError(isValidEmail(value) ? '' : emailError);
      return;
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handlePhoneBlur = () => {
    if (formData.phone && !isValidPhone(formData.phone)) {
      setPhoneError('Telefone inválido. Use (XX) XXXX-XXXX ou (XX) XXXXX-XXXX');
    } else {
      setPhoneError('');
    }
  };

  const handleEmailBlur = () => {
    if (formData.email && !isValidEmail(formData.email)) {
      setEmailError('E-mail inválido');
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const pErr =
      formData.phone && !isValidPhone(formData.phone)
        ? 'Telefone inválido. Use (XX) XXXX-XXXX ou (XX) XXXXX-XXXX'
        : '';
    const eErr = !isValidEmail(formData.email) ? 'E-mail inválido' : '';
    setPhoneError(pErr);
    setEmailError(eErr);
    if (pErr || eErr) return;
    setSubmitting(true);
    try {
      const resp = await submitContact({
        nome: formData.name,
        email: formData.email,
        telefone: formData.phone || undefined,
        assunto: formData.subject,
        mensagem: formData.message
      });
      if (resp?.success) {
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: '',
          message: ''
        });
        setModal({
          open: true,
          variant: 'success',
          title: 'Mensagem enviada!',
          message: 'Recebemos seu contato e retornaremos em breve. Obrigado!'
        });
      } else {
        setModal({
          open: true,
          variant: 'error',
          title: 'Falha no envio',
          message:
            'Não foi possível enviar sua mensagem. Verifique os dados e tente novamente.'
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Contato"
        description="Entre em contato com a Elite Prime Imobiliária. Tire suas dúvidas, agende visitas ou solicite informações sobre imóveis em Sorocaba e região."
      />
      {submitting && <Loading />}
      <FeedbackModal
        open={modal.open}
        variant={modal.variant}
        title={modal.title}
        message={modal.message}
        onClose={() => setModal((m) => ({ ...m, open: false }))}
      />
      <Header />
      <Navbar />
      <Styles.Main>
        <Styles.LabelTipo>
          Faça-nos uma visita ou mande-nos uma mensagem usando o formulário
          abaixo.
        </Styles.LabelTipo>

        <Styles.Container>
          {/* Transforme Styles.FormContainer em <form> no styled se desejar */}
          <Styles.FormContainer as="form" onSubmit={handleSubmit}>
            <Styles.InputContainer>
              <Styles.Label className="required">Nome</Styles.Label>
              <Styles.Input
                name="name"
                type="text"
                placeholder="Seu nome"
                required
                value={formData.name}
                onChange={handleChange}
                autoComplete="name"
              />
            </Styles.InputContainer>

            <Styles.InputContainer>
              <Styles.Label className="required">E-mail</Styles.Label>
              <Styles.Input
                name="email"
                type="email"
                placeholder="Seu e-mail"
                required
                value={formData.email}
                onChange={handleChange}
                onBlur={handleEmailBlur}
                autoComplete="email"
              />
              {emailError && (
                <small
                  style={{
                    color: '#d32f2f',
                    fontSize: '12px',
                    marginTop: '2px'
                  }}>
                  {emailError}
                </small>
              )}
            </Styles.InputContainer>

            <Styles.InputContainer>
              <Styles.Label className="required">Telefone</Styles.Label>
              <Styles.Input
                name="phone"
                type="text"
                placeholder="(XX) XXXXX-XXXX"
                value={formData.phone}
                onChange={handleChange}
                onBlur={handlePhoneBlur}
                autoComplete="tel"
              />
              {phoneError && (
                <small
                  style={{
                    color: '#d32f2f',
                    fontSize: '12px',
                    marginTop: '2px'
                  }}>
                  {phoneError}
                </small>
              )}
            </Styles.InputContainer>

            <Styles.InputContainer>
              <Styles.Label className="required">Assunto</Styles.Label>
              <Styles.Input
                name="subject"
                type="text"
                placeholder="Assunto"
                required
                value={formData.subject}
                onChange={handleChange}
              />
            </Styles.InputContainer>

            <Styles.InputContainer>
              <Styles.Label className="required">Mensagem</Styles.Label>
              <Styles.TextArea
                name="message"
                placeholder="Sua mensagem"
                required
                value={formData.message}
                onChange={handleChange}
              />
            </Styles.InputContainer>

            <Styles.RequiredText>Obrigatório</Styles.RequiredText>
            <Styles.Button
              type="submit"
              disabled={submitting}
              aria-busy={submitting}>
              {submitting ? 'ENVIANDO...' : 'ENVIAR'}
            </Styles.Button>
          </Styles.FormContainer>

          {!isMobile && (
            <>
              <Styles.Divider />
              <Styles.InfoContainer>
                <Styles.Logo src={logoUri} />
                <Styles.BotSide>
                  <Styles.MapContainer>
                    <iframe
                      src={`https://www.google.com/maps?q=${encodeURIComponent(endereco)}&output=embed`}
                      style={{ border: '0', borderRadius: '8px' }}
                      aria-hidden="false"
                      tabIndex={0}
                    />
                  </Styles.MapContainer>

                  <Styles.ContactInfo>
                    <Styles.ContactBox style={{ maxHeight: '55px' }}>
                      <LocationOnOutlinedIcon
                        sx={{
                          width: '25px',
                          height: '25px',
                          color: '#31cb00ff'
                        }}
                      />
                      <Styles.Text style={{ paddingLeft: '2px' }}>
                        {endereco}
                      </Styles.Text>
                    </Styles.ContactBox>

                    <Styles.ContactBox style={{ marginTop: '10px' }}>
                      <LocalPhoneOutlinedIcon
                        sx={{
                          width: '25px',
                          height: '25px',
                          color: '#31cb00ff'
                        }}
                      />
                      <Styles.Text>{whatsapp}</Styles.Text>
                    </Styles.ContactBox>
                    <Styles.ContactBox style={{ marginTop: '5px' }}>
                      <EmailOutlinedIcon
                        sx={{
                          width: '25px',
                          height: '25px',
                          color: '#31cb00ff'
                        }}
                      />
                      <Styles.Text>{emailEmpresa}</Styles.Text>
                    </Styles.ContactBox>
                  </Styles.ContactInfo>
                </Styles.BotSide>
              </Styles.InfoContainer>
            </>
          )}
        </Styles.Container>
      </Styles.Main>
      <Footer />
    </>
  );
};

export default ContactScreen;
